import { createRouter, createWebHistory } from "vue-router";
import NProgress from "nprogress";
import NotFound from "@/pages/not-found/NotFound.vue";
import { clientRoutes } from "./privateRoutes";
import store from "@/shared/store";
import i18n from "@/shared/config/i18n";

/* -------------------------------------------------------
 *  FILTER ROUTES BY meta.permissions
 * ----------------------------------------------------- */

function filterRoutesByPermissions(routes, scopes, userRole) {
  return routes
    .map((route) => {
      const allowed = checkRoutePermissions(route, scopes, userRole);

      if (!allowed) return null;

      if (route.children && route.children.length > 0) {
        const filteredChildren = filterRoutesByPermissions(
          route.children,
          scopes,
          userRole
        );

        if (filteredChildren.length > 0) {
          return {
            ...route,
            children: filteredChildren,
            redirect: fixRedirect(route, filteredChildren),
          };
        } else {
          // Удаляем пустые группы
          // eslint-disable-next-line no-unused-vars
          const { children, redirect, ...cleanRoute } = route;
          return cleanRoute;
        }
      }

      return route;
    })
    .filter(Boolean);
}

function checkRoutePermissions(route, scopes, userRole) {
  if (!route.meta) return true;

  // Role check: meta.roles = ["ADMIN", "NOTARY"]
  if (route.meta.roles) {
    if (!userRole || !route.meta.roles.includes(userRole)) return false;
  }

  // AND logic: meta.permissions = ["AVU"] or "AVU"
  if (route.meta.permissions) {
    const required = route.meta.permissions;
    if (typeof required === "string" && !scopes.includes(required))
      return false;
    if (Array.isArray(required) && !required.every((p) => scopes.includes(p)))
      return false;
  }

  // OR logic: meta.permissionsAny = ["TVNT", "IVND"]
  if (route.meta.permissionsAny) {
    const any = route.meta.permissionsAny;
    if (Array.isArray(any) && !any.some((p) => scopes.includes(p)))
      return false;
  }

  return true;
}

function fixRedirect(route, children) {
  if (!route.redirect) return undefined;

  const currentTarget = route.redirect.name;

  const exists = children.some((child) => child.name === currentTarget);

  if (exists) return route.redirect;

  // Берём первый "нормальный" child, у которого нет динамичного path
  const firstValid = children.find(
    (child) => child.name && !child.path.includes(":")
  );

  return firstValid ? { name: firstValid.name } : undefined;
}

/* -------------------------------------------------------
 *  CREATE ROUTER
 * ----------------------------------------------------- */

export const getRoutes = () => {
  const scopes = store.state.scopes || [];
  const userRole = store.state.self_user?.role;

  const rawRoutes = [
    ...clientRoutes,
    { path: "/:pathMatch(.*)*", name: "NotFound", component: NotFound },
  ];

  // 🔥 1) Фильтрация маршрутов по meta.permissions, meta.permissionsAny, meta.roles
  const filteredRoutes = filterRoutesByPermissions(rawRoutes, scopes, userRole);

  const router = createRouter({
    history: createWebHistory(),
    routes: filteredRoutes,
  });

  /* -------------------------------------------------------
   * 2) ROUTER GUARD — защита прямых URL
   * ----------------------------------------------------- */

  router.beforeEach((to, from, next) => {
    // Role guard
    if (to.meta.roles) {
      const userRole = store.state.self_user?.role;
      if (!userRole || !to.meta.roles.includes(userRole))
        return next({ name: "NotFound" });
    }

    // Permissions AND guard
    if (to.meta.permissions) {
      const scopes = store.state.scopes;
      const required = to.meta.permissions;
      const allowed = Array.isArray(required)
        ? required.every((p) => scopes.includes(p))
        : scopes.includes(required);
      if (!allowed) return next({ name: "NotFound" });
    }

    // Permissions OR guard
    if (to.meta.permissionsAny) {
      const scopes = store.state.scopes;
      const any = to.meta.permissionsAny;
      if (Array.isArray(any) && !any.some((p) => scopes.includes(p)))
        return next({ name: "NotFound" });
    }

    next();
  });

  router.beforeEach((to, from, next) => {
    if (to.meta?.title) {
      document.title = i18n.global.t(to.meta.title);
    }
    next();
  });

  router.beforeResolve((to, from, next) => {
    if (to.name) NProgress.start();
    next();
  });

  router.afterEach(() => NProgress.done());

  return router;
};
